import React, { useCallback, useContext, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import AppPaths from '../../AppPaths';
import { ReactComponent as AddIcon } from '../../assets/images/icons/add.svg';
import Button from '../../components/Button';
import CartSummary from '../../components/CartSummary';
import ItemsInCart from '../../components/ItemsInCart';
import HardwareFirstCartLayout from '../../components/HardwareFirstCart/HardwareFirstCartLayout';
import HardwareFirstUpsell from '../../components/HardwareFirstCart/HardwareFirstUpsell';
import HardwareFirstEmptyCart from '../../components/HardwareFirstCart/HardwareFirstEmptyCart';
import LegacyHardwareFirstCartLayout from '../../components/HardwareFirstCartLegacy/HardwareFirstCartLayout';
import LegacyHardwareFirstUpsell from '../../components/HardwareFirstCartLegacy/HardwareFirstUpsell';
import { CartPricingProvider, useCartPricing } from '../../contexts/CartPricingContext';
import * as events from '../../lib/analytics/events';
import CheckoutContext, { CheckoutContextProvider } from '../../lib/CheckoutContext';
import { MarketingOptInProvider } from '../../contexts/MarketingOptInContext';
import { CouponError } from '../../lib/errors';
import { priceCartForPreview } from '../../lib/pricing';
import * as types from '../../types';
import styles from './Cart.module.scss';
import MultidogDiscountExistingCollars from '../../components/MultidogDiscount';
import { useDispatch, useSelector } from 'react-redux';
import CartValueProps from '../../components/CartSummary/CartValueProps';
import { showNanoVariant } from '../../components/NanoBanner/NanoBanner';
import {
  inCartMiniCollarCount,
  inCartPrepaidMembershipCount,
  inCartS3CollarCount,
} from '../../components/ItemsInCart/CouponLineItemV2';
import { getFiGrowthBook } from '../../lib/growthbook';
import { cartActions } from '../../reducers/storeCart';
import OllieWelcomeBoxImage from '../../assets/images/ollie-welcome-box.png';
import { ShoppingBagIcon } from '../../components/AppBar/ShoppingBag';
import classNames from 'classnames';
import CommunityIcon from '../../assets/images/icons/icon_community.svg';
import ArrowIcon from '../../assets/images/icons/icon_arrow.svg';
import { useIsGift } from '../../lib/util/isGift';
import { isBundleCartItem } from '../../lib/cart';
import { isEnrolledInPositionDiscount } from '../../lib/positionDiscount';
import useCartHasFreeTrialBundle from '../../hooks/useCartHasFreeTrialBundle';
import useCartHasOnlyFreeTrialBundles from '../../hooks/useCartHasOnlyFreeTrialBundles';
import useHardwareFirstCartUnits from '../../hooks/useHardwareFirstCartUnits';
import useHardwareFirstCheckout from '../../hooks/useHardwareFirstCheckout';
import useCartCheckoutV2 from '../../hooks/useCartCheckoutV2';
import { HARDWARE_FIRST_MAX_UNITS_PER_CART } from '../../lib/hardwareFirst';
import { useLocale } from '../../contexts/LocaleContext';
import useIsReferralCheckoutV2 from '../../hooks/useIsReferralCheckoutV2';
import ReferralTestimonials from '../../components/ReferralTestimonials/ReferralTestimonials';
import { showRescuePdpRedesign } from '../RescueLanding/RescueBanner';

function OllieWelcomeBox() {
  const { t } = useTranslation();
  return (
    <div className={styles.ollieWelcomeBox}>
      <div className={styles.ollieWelcomeBoxContainer}>
        <img className={styles.ollieWelcomeBoxImage} src={OllieWelcomeBoxImage} alt={t('ollie_welcome_box')} />
        <div className={styles.ollieWelcomeBoxContent}>
          <div className={styles.ollieWelcomeBoxTitle}>{t('ollie_starter_box')}</div>
          <div className={styles.ollieWelcomeBoxDescription}>
            {t('with_your_fi_membership_you_will_receive_a_coupon_for_2_weeks_of_free_dog_food_with_ollie_175_value')}
          </div>
        </div>
      </div>
      <div className={styles.ollieWelcomeBoxPrice}>{t('free_uppercase')}</div>
    </div>
  );
}

function AddAnotherCollarButton() {
  const isGift = useIsGift();
  const { t } = useTranslation();
  const history = useHistory();
  const { cart, session } = useContext(CheckoutContext);
  const hasS3CollarInCart = inCartS3CollarCount(Object.values(cart.cartItems)) > 0;
  const multiDogDiscountAmount = useSelector((state: types.AppState) => state.config.siteConfig.multiDogDiscountAmount);
  const multiDogHouseholdDiscountEnabled = useSelector(
    (state: types.AppState) => state.config.siteConfig.multiDogHouseholdDiscountEnabled ?? false,
  );
  const isLoggedIn = !!session;

  if (!hasS3CollarInCart) {
    return null;
  }

  return (
    <div className={styles.cartItemsContainer}>
      <div className={styles.addItemsButtonContainer}>
        <Button
          className={styles.addItemsButton}
          secondary
          onClick={() => {
            history.push(AppPaths.Home);
          }}
        >
          <AddIcon />
          {t('add_another_collar')}
        </Button>
        {multiDogHouseholdDiscountEnabled && !!multiDogDiscountAmount && !isGift && (
          <>
            <div className={styles.multidogDiscountBanner}>
              <div className={styles.multidogDiscountBannerBoldText}>{t('have_multiple_dogs')}</div>
              <div>
                {t('get_percent_off_all_fi_memberships_no_activation_fee', { percent: multiDogDiscountAmount })}
              </div>
            </div>
            {isLoggedIn && <MultidogDiscountExistingCollars />}
          </>
        )}
      </div>
    </div>
  );
}

function AddAnotherCollarButtonV2() {
  const { t } = useTranslation();
  const history = useHistory();
  const { cart } = useContext(CheckoutContext);
  const cartPricing = useCartPricing();
  const hasS3CollarInCart = inCartS3CollarCount(Object.values(cart.cartItems)) > 0;
  const hasMiniCollarInCart = inCartMiniCollarCount(Object.values(cart.cartItems)) > 0;
  const isAllGifts = Object.values(cart.cartItems).every((item) => {
    return isBundleCartItem(item) ? item.isGift : false;
  });
  const multiDogDiscountAmount = useSelector((state: types.AppState) => state.config.siteConfig.multiDogDiscountAmount);
  const multiDogHouseholdDiscountEnabled = useSelector(
    (state: types.AppState) => state.config.siteConfig.multiDogHouseholdDiscountEnabled ?? false,
  );

  const hasFreeTrialBundle = useCartHasFreeTrialBundle();
  const isEnrolledInPositionDiscountExperiment = isEnrolledInPositionDiscount(cartPricing);

  if (!hasS3CollarInCart && !hasMiniCollarInCart) {
    return null;
  }

  const isGiftParam = isAllGifts ? '?isGift=true' : '';
  const appPath = hasS3CollarInCart ? AppPaths.Home : AppPaths.Products('fi-mini') + isGiftParam;

  const showMultiDogDiscount = multiDogHouseholdDiscountEnabled && !!multiDogDiscountAmount;
  const discountText = t('get_percent_off_all_fi_memberships_no_activation_fee', { percent: multiDogDiscountAmount });

  // position discount messaging - same layout as original, different text
  if (isEnrolledInPositionDiscountExperiment && !isAllGifts) {
    const maxPosition = cartPricing.positionDiscounts
      ? Math.max(...Object.values(cartPricing.positionDiscounts).map((d) => d.position), 0)
      : 0;
    const nextDiscountPercent = maxPosition + 1 === 2 ? 10 : 15;
    return (
      <div className={styles.cartItemsContainer}>
        <div className={classNames(styles.addAnotherCollarButtonV2, styles.positionDiscount)}>
          <div className={styles.addAnotherCollarButtonV2Image}>
            <img src={CommunityIcon} alt={t('community')} />
          </div>
          <div className={styles.addAnotherCollarButtonV2Content}>
            <div className={styles.addAnotherCollarButtonV2Title}>
              {t('multi_pet_upsell_next_membership', { percent: nextDiscountPercent })}
            </div>
            <div
              className={styles.addAnotherCollarButtonV2Description}
              onClick={() => {
                history.push(appPath);
              }}
            >
              {t('add_another_device')}
              <div className={styles.addAnotherCollarButtonV2Arrow}>
                <img src={ArrowIcon} alt={t('arrow')} />
              </div>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.cartItemsContainer}>
      <div className={styles.addAnotherCollarButtonV2}>
        <div className={styles.addAnotherCollarButtonV2Image}>
          <img src={CommunityIcon} alt={t('community')} />
        </div>
        <div className={styles.addAnotherCollarButtonV2Content}>
          <div className={styles.addAnotherCollarButtonV2Title}>
            {isAllGifts ? t('have_multiple_gifts') : t('have_multiple_pets')}
          </div>
          <div>
            {isAllGifts ? t('get_all_gifts_at_one_time') : showMultiDogDiscount && discountText}
            {!isAllGifts && showMultiDogDiscount && hasFreeTrialBundle && (
              <span className={styles.freeTrialDisclaimer}> {t('offer_excludes_free_trials')}</span>
            )}
          </div>
          <div
            className={styles.addAnotherCollarButtonV2Description}
            onClick={() => {
              history.push(appPath);
            }}
          >
            {isAllGifts ? t('add_another_gift') : t('add_another_device')}
            <div className={styles.addAnotherCollarButtonV2Arrow}>
              <img src={ArrowIcon} alt={t('arrow')} />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function useTracking({ cartItems }: types.Cart, skip: boolean) {
  const { currency } = useLocale();
  const [hasLoggedPageLoad, setHasLoggedPageLoad] = useState(false);
  useEffect(() => {
    if (!skip && !hasLoggedPageLoad && Object.keys(cartItems).length > 0) {
      events.cartPage.viewed(cartItems, currency);
      setHasLoggedPageLoad(true);
    }
  }, [cartItems, hasLoggedPageLoad, currency, skip]);
}

export function CartContainer() {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { cart, session } = useContext(CheckoutContext);
  const history = useHistory();
  const cartV2Enabled = showNanoVariant() || getFiGrowthBook().getFeatureValue('nano-ecom-test-1', false);
  const isCartEmpty = Object.keys(cart.cartItems).length <= 0;
  const isLoggedIn = !!session;
  const showOllieWelcomeBox =
    inCartPrepaidMembershipCount(Object.values(cart.cartItems)) > 0 &&
    getFiGrowthBook().getFeatureValue('ollie-welcome-box', false);
  const showEcomCartTest = getFiGrowthBook().getFeatureValue('ecom-cart-test', false);
  const hasOnlyFreeTrialBundles = useCartHasOnlyFreeTrialBundles();
  const hideMultiDogUpsell = hasOnlyFreeTrialBundles;
  // an adopter arrives to claim one gifted collar; a second-pet cross-sell between the item rows and
  // the totals interrupts that with a decision they did not come for
  const rescueGwaVariant = showRescuePdpRedesign();
  const isReferralV2 = useIsReferralCheckoutV2();
  const hardwareFirstEnrolled = useHardwareFirstCheckout();
  const cartCheckoutV2 = useCartCheckoutV2();
  const hardwareFirstUnits = useHardwareFirstCartUnits();
  const hardwareFirstAtCap = hardwareFirstUnits >= HARDWARE_FIRST_MAX_UNITS_PER_CART;

  // the arm reports its cart view from inside the pricing provider, where the repriced figures are
  useTracking(cart, hardwareFirstUnits > 0);

  const fetchPricing = useCallback(async () => {
    const options = {
      // the hardware-first layout never shows tax, so skip Recurly's tax calculation there
      excludeTax: hardwareFirstUnits > 0 || !(showEcomCartTest && isLoggedIn),
    };

    try {
      const result = await priceCartForPreview(cart, options);
      if (result.validationErrors?.couponCode) {
        // If the coupon code is invalid, remove it from the cart
        dispatch(cartActions.removeCoupon());

        // By returning undefined here, we won't display the pricing we just fetched which
        // could be stale since the coupon was invalid.
        // The action to remove the coupon from the cart above should cause a re-render,
        // so we will reload the pricing without displaying stale pricing
        return undefined;
      }

      return result;
    } catch (err) {
      if (err instanceof CouponError) {
        // When the pricing API rejects the coupon with an HTTP error (e.g. coupon incompatible
        // with free trial), remove the coupon and re-render instead of breaking the cart
        dispatch(cartActions.removeCoupon());
        return undefined;
      }
      throw err;
    }
  }, [showEcomCartTest, isLoggedIn, hardwareFirstUnits, cart, dispatch]);

  if (isCartEmpty) {
    // an empty cart has no units to count, so the arm's own empty state gates on enrollment
    if (hardwareFirstEnrolled) {
      return <HardwareFirstEmptyCart />;
    }

    return (
      <div className={styles.emptyCartContainer}>
        <div className={styles.main}>
          <div className={styles.leftContainer}>
            <h2>{t('your_cart')}</h2>
          </div>
        </div>
        <div className={styles.emptyCartContent}>
          <div className={styles.emptyCartIcon}>
            <ShoppingBagIcon />
          </div>
          <div className={styles.emptyCartTitle}>{t('nothing_here_yet')}</div>
          <div className={styles.emptyCartDescription}>
            {t('dont_worry_weve_got_just_the_thing_to_get_tails_wagging')}
          </div>
          <div className={styles.emptyCartButtonContainer}>
            <Button
              className={styles.emptyCartButton}
              onClick={() => {
                history.push(AppPaths.ProductListingPage);
              }}
            >
              {t('shop_fi')}
            </Button>
          </div>
        </div>
      </div>
    );
  }

  const onCheckOut = (checkoutCart: types.Cart, checkoutPricing: types.CartPricing) => {
    events.cartPage.checkout(checkoutCart.cartItems, checkoutPricing, checkoutCart.couponCode, checkoutCart.isGift);
  };

  // the arm replaces the page rather than restyling it - the control tree below is untouched.
  // the experiment picks the redesign or the snapshot of the cart it is tested against.
  if (hardwareFirstUnits > 0) {
    return cartCheckoutV2 ? (
      <HardwareFirstCartLayout
        atCap={hardwareFirstAtCap}
        fetchPricing={fetchPricing}
        onCheckOut={onCheckOut}
        upsell={<HardwareFirstUpsell />}
      />
    ) : (
      <LegacyHardwareFirstCartLayout
        atCap={hardwareFirstAtCap}
        fetchPricing={fetchPricing}
        onCheckOut={onCheckOut}
        upsell={<LegacyHardwareFirstUpsell />}
      />
    );
  }

  return (
    <div className={classNames(styles.main, { 'ecom-cart-test': showEcomCartTest })}>
      <div className={styles.leftContainer}>
        <h2>{t('your_cart')}</h2>
      </div>
      <div className={styles.rightContainer}>
        <CartPricingProvider fetchPricing={fetchPricing}>
          <div className={styles.cartItemsContainer}>
            <div className={styles.cartItems}>
              <ItemsInCart allowRemoveCartItem allowRemoveCode={!showEcomCartTest} />
            </div>
          </div>
          {showOllieWelcomeBox && <OllieWelcomeBox />}
          {cartV2Enabled && !showEcomCartTest && <CartValueProps />}
          {!showEcomCartTest && !rescueGwaVariant && !hardwareFirstAtCap && <AddAnotherCollarButton />}
          {showEcomCartTest && !rescueGwaVariant && !hardwareFirstAtCap && <AddAnotherCollarButtonV2 />}
          {hardwareFirstAtCap && !rescueGwaVariant && (
            <div className={styles.hardwareFirstBulkNotice}>
              {t('hardware_first_bulk_sales_message', { count: HARDWARE_FIRST_MAX_UNITS_PER_CART })}
            </div>
          )}
          {isReferralV2 && <ReferralTestimonials />}
          <CartSummary
            onCheckOut={(checkoutCart, cartPricing) => {
              events.cartPage.checkout(
                checkoutCart.cartItems,
                cartPricing,
                checkoutCart.couponCode,
                checkoutCart.isGift,
              );
            }}
            allowPromoCodes={!hideMultiDogUpsell}
            allowLogin={true}
          />
        </CartPricingProvider>
      </div>
    </div>
  );
}

export default function Cart() {
  // Cart needs to be wrapped with checkout context to handle Apple Pay, which is a one-click checkout from the cart
  return (
    <MarketingOptInProvider>
      <CheckoutContextProvider>
        <CartContainer />
      </CheckoutContextProvider>
    </MarketingOptInProvider>
  );
}
